import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { themes, ThemeName, Theme } from '../themes';

interface ThemeContextType {
  theme: Theme;
  themeName: ThemeName;
  toggleTheme: () => void;
  setTheme: (themeName: ThemeName) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'novawarfare-theme';

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeName, setThemeName] = useState<ThemeName>(() => {
    // Load saved theme from localStorage or use shadowGrid default
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (savedTheme === 'fieldCommand') {
      return 'shadowGrid';
    }
    return savedTheme && (savedTheme in themes)
      ? (savedTheme as ThemeName)
      : 'shadowGrid';
  });

  const [theme, setThemeState] = useState<Theme>(themes[themeName]);

  useEffect(() => {
    // Update theme state when themeName changes
    setThemeState(themes[themeName]);
    // Save to localStorage
    localStorage.setItem(THEME_STORAGE_KEY, themeName);
  }, [themeName]);

  const toggleTheme = () => {
    // Order: Shadow Grid → Nova Frontier → Night Ops → Shadow Grid
    setThemeName(prev => {
      if (prev === 'shadowGrid') return 'modernWarfare';
      if (prev === 'modernWarfare') return 'nightOps';
      return 'shadowGrid';
    });
  };

  const setTheme = (name: ThemeName) => {
    setThemeName(name);
  };

  return (
    <ThemeContext.Provider value={{ theme, themeName, toggleTheme, setTheme }}>
      <StyledThemeProvider theme={theme}>
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};
